import { type Transition } from "motion/react";
import { type ReactNode, useCallback, useState } from "react";
import { EASE_OUT, SPRING_SWAP } from "@/lib/ease";

"use client";

export const SPRING_MORPH = { type: "spring", stiffness: 380, damping: 28, mass: 0.8 } as const;

export const MORPH_ENTER: Transition = { ...SPRING_MORPH, opacity: { duration: 0.1 } };

export const MORPH_EXIT: Transition = {
  type: "spring",
  stiffness: 520,
  damping: 40,
  mass: 0.6,
  opacity: { duration: 0.12, delay: 0.1 },
};

export const SEED = { width: 112, height: 40 };

export const SEED_TUCK = 28;

export const CONTENT_IN: Transition = { duration: 0.18, ease: EASE_OUT, delay: 0.12 };

export const CONTENT_OUT: Transition = { duration: 0.08, ease: EASE_OUT };

export const GLASS =
  "border border-foreground/[0.08] bg-white/55 backdrop-blur-[20px] backdrop-saturate-[1.6] shadow-[inset_0_1px_0_0_rgb(255_255_255/0.06),0_24px_60px_-24px_rgb(0_0_0/0.45)]";

export const CHIP_HIDDEN = { opacity: 0, scale: 0.6, filter: "blur(4px)" };

export const CHIP_SHOWN = { opacity: 1, scale: 1, filter: "blur(0px)" };

export const CHIP_ENTER: Transition = {
  ...SPRING_SWAP,
  opacity: { duration: 0.16 },
  filter: { duration: 0.2 },
};

export const CHIP_EXIT: Transition = { duration: 0.16, ease: EASE_OUT };

export const SURFACE_RADIUS = 12;

export const PANEL_ROOM = 320;

export type Row = { id: string; label: string; kind: "query" | "recent" | "suggestion" };

export type Side = "top" | "bottom";

export interface MorphingSearchChipsProps {
  id?: string;
  addLabel?: string;
  /** Active filters shown as chips inside the bar. */
  chips?: string[];
  defaultChips?: string[];
  onChipsChange?: (chips: string[]) => void;
  /** Text in the field. */
  query?: string;
  defaultQuery?: string;
  onQueryChange?: (query: string) => void;
  /** Recent searches, newest first. */
  recents?: string[];
  defaultRecents?: string[];
  onRecentsChange?: (recents: string[]) => void;
  /** Persist uncontrolled recents in localStorage under this key. */
  storageKey?: string;
  /** How many recent searches to keep. Default 5. */
  maxRecents?: number;
  /** Terms offered while typing. */
  suggestions?: string[];
  /**
   * How suggestions match the query. Default: case-insensitive substring.
   * Pass `false` when `suggestions` already arrive filtered, e.g. from a server.
   */
  filter?: ((term: string, query: string) => boolean) | false;
  /** How many matches to list under the typed query. Default 6. */
  maxSuggestions?: number;
  /** Suggestions are being fetched. */
  loading?: boolean;
  /** Most chips allowed at once. */
  maxChips?: number;
  /** Clean a term before it becomes a chip; return null to reject it. */
  normalize?: (term: string) => string | null;
  /** Shown when the field is empty and there are no recent searches. */
  emptyState?: ReactNode;
  /** Which way the surface grows. "auto" grows up unless there is no room. Default "top". */
  side?: "top" | "bottom" | "auto";
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** Submit each chip with a form under this name. */
  name?: string;
  placeholder?: string;
  /** Accessible name for the search field. */
  label?: string;
  /** Trailing controls inside the bar, such as filter buttons. */
  actions?: ReactNode;
  className?: string;
}

export function useControllable<T>(value: T | undefined, initial: T, onChange?: (next: T) => void) {
  const [internal, setInternal] = useState(initial);
  const current = value ?? internal;
  const set = useCallback(
    (next: T) => {
      if (value === undefined) setInternal(next);
      onChange?.(next);
    },
    [value, onChange],
  );
  return [current, set, setInternal] as const;
}

export const sameTerm = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export const contains = (term: string, query: string) => term.toLowerCase().includes(query.toLowerCase());

export function Highlight({ text, query }: { text: string; query: string }) {
  const at = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-semibold">{text.slice(at, at + query.length)}</span>
      {text.slice(at + query.length)}
    </>
  );
}
